import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Icon } from '../ui/Icon';
import { eur, type Arhes } from '../data/repository';
import { REGLAGES } from '../data/mock-data';

/**
 * ÉLÉMENTS PARTAGÉS ENTRE PAGES
 * Un fil d'Ariane, un en-tête de section, et les ré-exports utiles : trois choses,
 * pas une bibliothèque de plus.
 */
export { classeBouton as clsBouton, Note, Accordéon, Bouton, Etiquette } from '../ui/kit';

export function Fil({ fils }: { fils: Array<{ libelle: string; vers?: string }> }) {
  return (
    <nav aria-label="Fil d’Ariane" className="mb-5">
      <ol className="flex flex-wrap items-center gap-1.5 text-[0.8125rem] text-muted">
        <li><Link to="/" className="transition hover:text-ink">Accueil</Link></li>
        {fils.map((f, i) => (
          <li key={`${f.libelle}-${i}`} className="flex items-center gap-1.5">
            <Icon name="chevronRight" size={13} className="text-linen-deep" aria-hidden="true" />
            {f.vers ? (
              <Link to={f.vers} className="transition hover:text-ink">{f.libelle}</Link>
            ) : (
              <span className="font-medium text-ink-soft" aria-current="page">{f.libelle}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function Entete({ surtitre, titre, texte, lien, centre }: {
  surtitre: string; titre: string; texte?: ReactNode; lien?: { vers: string; libelle: string }; centre?: boolean;
}) {
  return (
    <div className={centre ? 'text-center' : 'flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'}>
      <div className={centre ? 'mx-auto max-w-2xl' : 'max-w-2xl'}>
        <p className="eyebrow">{surtitre}</p>
        <h2 className="mt-2 text-[clamp(1.8rem,3.2vw,2.6rem)]">{titre}</h2>
        {texte && <p className="mt-2.5 text-[0.9375rem] leading-relaxed text-muted">{texte}</p>}
      </div>
      {lien && (
        <Link to={lien.vers}
          className="group inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-ink underline-offset-4 hover:underline">
          {lien.libelle}
          <Icon name="arrowRight" size={16} className="transition group-hover:translate-x-0.5" />
        </Link>
      )}
    </div>
  );
}


/**
 * BLOC DE PAIEMENT — la question que le client se pose vraiment :
 * « je paie combien maintenant, et combien au comptoir ? »
 *
 * Il est monté **une seule fois** et repris par le panier, le tiroir et le
 * tunnel : un seul endroit à corriger, donc un seul chiffre possible à l'écran.
 * Les montants viennent de `arrhes()` (règle publiée), jamais d'une phrase.
 */
export function BlocPaiement({ total, ar }: { total: number; ar: Arhes }) {
  const reste = Math.max(0, Math.round((total - ar.montant) * 100) / 100);
  const bas = Math.round(total * (1 - REGLAGES.poids.tolerancePct / 100) * 100) / 100;
  const haut = Math.round(total * (1 + REGLAGES.poids.tolerancePct / 100) * 100) / 100;

  return (
    <div className="rounded-2xl bg-brass-wash/70 p-4">
      <dl className="space-y-1.5 text-[0.875rem]">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-brass-ink">Aujourd’hui, en ligne</dt>
          <dd className="font-semibold tabular-nums text-brass-ink">
            {ar.montant > 0 ? eur(ar.montant) : 'Rien à payer'}
            {ar.pourcentage != null && ar.pourcentage > 0 && ar.pourcentage < 100 && (
              <span className="ml-1 text-[0.75rem] font-normal">— arrhes {ar.pourcentage} %</span>
            )}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-brass-ink">{ar.regle === 'R6' ? 'Livraison réglée en ligne' : 'Au retrait, après pesée'}</dt>
          <dd className="tabular-nums text-brass-ink">
            {ar.regle === 'R6' ? 'inclus' : `≈ ${eur(reste)}`}
          </dd>
        </div>
      </dl>

      <p className="mt-3 flex items-start gap-1.5 border-t border-brass-ink/15 pt-3 text-[0.75rem] leading-relaxed text-brass-ink/85">
        <Icon name="scale" size={13} className="mt-0.5 shrink-0" />
        <span>
          Montant final entre <strong className="font-semibold tabular-nums">{eur(bas)}</strong> et{' '}
          <strong className="font-semibold tabular-nums">{eur(haut)}</strong> : le prix suit le poids réel,
          dans la tolérance de ±{REGLAGES.poids.tolerancePct} %. Au-delà, la maison absorbe l’écart.
        </span>
      </p>

      <details className="group mt-2">
        <summary className="flex cursor-pointer items-center gap-1.5 text-[0.75rem] font-medium text-brass-ink underline-offset-4 hover:underline [&::-webkit-details-marker]:hidden">
          <Icon name="info" size={13} />
          Pourquoi ce paiement d’avance ?
        </summary>
        <p className="mt-2 text-[0.75rem] leading-relaxed text-brass-ink/85">
          {ar.explication}
          {ar.regle !== 'R6' && (
            <> Votre pièce est achetée et découpée pour vous : les arrhes couvrent ce qui serait perdu en
            cas de non-retrait. Elles vous sont rendues si vous annulez plus de{' '}
            {REGLAGES.delais.annulationH} h avant le créneau. Client de confiance : la demande baisse
            automatiquement, voire disparaît.</>
          )}
        </p>
      </details>
    </div>
  );
}
